import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, Package, MessageSquare, BarChart3, Globe, Zap,
  ArrowRight, CheckCircle, Code2, Brain, Store, LineChart
} from 'lucide-react';

export default function SignalsPage() {
  return (
    <div className="min-h-screen bg-[#0B1120]">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: 'radial-gradient(60% 60% at 50% 30%, rgba(122,92,255,0.18) 0%, rgba(122,92,255,0) 60%)'
          }}
        />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-3xl">
            <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30 mb-6">
              Commerce Index Signals
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Commerce Signals,
              <span className="text-[#3EF1C7]"> On Tap</span>
            </h1>
            <p className="text-xl text-[#94A3B8] mb-8 leading-relaxed">
              API-first access to demand, pricing, and activity signals across markets — 
              built on the Commerce Index event graph.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                className="bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 text-lg px-8 py-6"
                data-testid="signals-cta-access"
              >
                Request API Access
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Link to="/docs/signals">
                <Button 
                  variant="outline" 
                  className="border-[#334155] text-[#E2E8F0] hover:bg-[#1E293B] text-lg px-8 py-6"
                  data-testid="signals-cta-docs"
                >
                  View API Docs
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Why Commerce Needs Signals, Not Just Dashboards
            </h2>
            <p className="text-lg text-[#94A3B8]">
              Teams are constantly asking questions that turn into ad hoc analysis, fragile spreadsheets, 
              or "we don't actually know."
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              "What's really happening in my category in this country?",
              "Where is demand shifting for products like ours?",
              "Are we operating in a full-price environment or a discount war?",
              "How do our patterns compare to the rest of the market?"
            ].map((question, i) => (
              <div key={i} className="p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
                <p className="text-[#E2E8F0] font-medium">"{question}"</p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-xl text-[#3EF1C7] font-medium">
              Commerce Index Signals turns these questions into a simple API call.
            </p>
          </div>
        </div>
      </section>

      {/* Signal Types */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
              Signal Types
            </Badge>
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Three Layers of Intelligence
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <TrendingUp className="w-10 h-10 text-[#3EF1C7] mb-4" />
                <CardTitle className="text-white text-xl">Brand Signals</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-[#94A3B8]">
                  Aggregated view of your brand's activity: views, purchases, conversion, trends, and top products.
                </p>
                <div className="space-y-2">
                  {[
                    'total_views, total_purchases',
                    'conversion_rate',
                    'trend vs previous period',
                    'top_products ranking'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-[#E2E8F0]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#3EF1C7]" />
                      {item}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <Package className="w-10 h-10 text-[#7A5CFF] mb-4" />
                <CardTitle className="text-white text-xl">Product Signals</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-[#94A3B8]">
                  Product-level metrics and an activity tier assessment for individual SKUs.
                </p>
                <div className="space-y-2">
                  {[
                    'views, purchases, conversion',
                    'trend direction (up/flat/down)',
                    'activity_tier (high/medium/low)',
                    'human-readable comment'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-[#E2E8F0]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#7A5CFF]" />
                      {item}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <MessageSquare className="w-10 h-10 text-[#FFD166] mb-4" />
                <CardTitle className="text-white text-xl">Context Signals</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-[#94A3B8]">
                  Compressed, opinionated summary with headline and actionable recommendations.
                </p>
                <div className="space-y-2">
                  {[
                    'one-line headline summary',
                    'key_signals structured data',
                    'recommendations array',
                    'confidence + derived_from'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-[#E2E8F0]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#FFD166]" />
                      {item}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Who Uses CI Signals
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: BarChart3,
                title: 'Pricing & Revenue Teams',
                desc: 'Use category signals to inform pricing experiments and understand if the market is in promo-heavy or full-price mode.',
                color: '#3EF1C7'
              },
              {
                icon: Brain,
                title: 'AI Agent Platforms',
                desc: 'Feed signals to agents so they make market-aware decisions, not just react to local data.',
                color: '#7A5CFF'
              },
              {
                icon: Store,
                title: 'Marketplace Platforms',
                desc: 'Compare on-platform trends to off-platform signals. Steer category investments with confidence.',
                color: '#FFD166'
              }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="bg-[#1E293B] border-[#334155]">
                  <CardHeader>
                    <Icon className="w-10 h-10 mb-2" style={{ color: item.color }} />
                    <CardTitle className="text-white">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#94A3B8]">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Code Example */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
                Simple API
              </Badge>
              <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                One API Call to Structured Intelligence
              </h2>
              <p className="text-lg text-[#94A3B8] mb-6">
                No need to collect cross-brand events, match products across merchants, or compute category indices. 
                Get clean, named signals maintained like an actual product.
              </p>
              <div className="space-y-3">
                {[
                  'Built on the growing CI event graph',
                  'Includes confidence metadata',
                  'Versioned API with backward compatibility',
                  'Rate limits designed for production use'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-[#E2E8F0]">
                    <CheckCircle className="w-5 h-5 text-[#3EF1C7]" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-[#0D1628] rounded-xl border border-[#22304A] p-6">
              <div className="flex items-center gap-2 mb-4">
                <Code2 className="w-5 h-5 text-[#3EF1C7]" />
                <span className="text-[#64748B] text-sm">Example: Brand Signals</span>
              </div>
              <pre className="text-sm text-[#E2E8F0] overflow-x-auto">
                <code>{`GET /api/v1/signals/brand?brand_id=brand_xyz&window=7d
X-CI-Api-Key: <your-api-key>

// Response
{
  "brand_id": "brand_xyz",
  "time_window": "last_7_days",
  "aggregate": {
    "total_views": 12845,
    "total_purchases": 642,
    "overall_conversion_rate": 0.05
  },
  "trend": {
    "views_vs_prev_period": "up",
    "views_change_pct": 12.3
  },
  "meta": {
    "confidence": "high"
  }
}`}</code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Future Signals */}
      <section className="py-20 bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 mb-4">
              Roadmap
            </Badge>
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Coming Soon: Market & Category Signals
            </h2>
            <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
              As our event graph grows, we'll unlock market-level and category-level signals across countries.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="bg-[#1E293B] border-[#334155] opacity-75">
              <CardHeader>
                <Globe className="w-8 h-8 text-[#64748B] mb-2" />
                <CardTitle className="text-[#94A3B8]">Market Signals</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#64748B] text-sm">
                  commerce_health, demand_trend, discount_pressure, category_hotspots per country
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1E293B] border-[#334155] opacity-75">
              <CardHeader>
                <LineChart className="w-8 h-8 text-[#64748B] mb-2" />
                <CardTitle className="text-[#94A3B8]">Category Signals</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#64748B] text-sm">
                  demand_index, supply_index, pricing_env, merchant_health, momentum by category
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#1E293B] to-[#0D1628] rounded-2xl p-12 border border-[#334155] text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Ready to Build on Commerce Intelligence?
            </h2>
            <p className="text-lg text-[#94A3B8] mb-8 max-w-2xl mx-auto">
              If you need better answers than "let's pull another spreadsheet," CI Signals is the simplest way 
              to bring real commerce intelligence into your stack.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                className="bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 text-lg px-8 py-6"
                data-testid="signals-cta-request"
              >
                Request API Access
              </Button>
              <Link to="/docs/signals">
                <Button 
                  variant="outline" 
                  className="border-[#334155] text-[#E2E8F0] hover:bg-[#1E293B] text-lg px-8 py-6"
                >
                  View API Examples
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
